/**
 * Returns which theme variant should be active for the current route + org FF state.
 *
 * Rules:
 *  - OPR pages (/:org/open-reporting/*) → always 'vault' (regardless of FF)
 *  - FF ATLAS_THEME enabled → 'atlas'
 *  - FF ATLAS_THEME disabled / not set → 'vault' (safe default)
 *
 * Temporary hook — remove when ATLAS_THEME FF is retired.
 * See removal checklist in `.cursor/atlas-ff-plan.md`.
 */

import { useSelector } from 'react-redux';
import { ATLAS_THEME_LS_KEY } from 'src/creators/Feature.creators';
import { FeatureFlag, State } from 'src/types';
import { useIsFeatureEnabled } from './useIsFeatureEnabled';
import { useOpenReportingLocation } from './useOpenReportingLocation';

export type AtlasThemeVariant = 'atlas' | 'vault';

function getCachedAtlasVariant(): AtlasThemeVariant {
  try {
    return localStorage.getItem(ATLAS_THEME_LS_KEY) === 'true'
      ? 'atlas'
      : 'vault';
  } catch {
    return 'vault';
  }
}

export function useAtlasThemeVariant(): AtlasThemeVariant {
  const isFetching = useSelector(({ fetching }: State) => fetching?.isFetching);
  const isAtlasEnabled = useIsFeatureEnabled(FeatureFlag.ATLAS_THEME);
  const { isOpenReporting } = useOpenReportingLocation();

  if (isOpenReporting) return 'vault';
  // During the session fetch, Redux features aren't loaded yet.
  // Use the value cached from the previous session to avoid a theme flash.
  if (isFetching) return getCachedAtlasVariant();
  return isAtlasEnabled ? 'atlas' : 'vault';
}
